import { CollectionSyncState } from '../state/types';
import { createSyncActionTypeValue } from '../actionTypes/lib';
import { SyncVerb } from '../types/Verbs';

import {
	AddCollectionSyncSliceOptions,
	CreateSyncReduxOptions,
	RemoveCollectionSyncReduxOptions,
	UnionedCollectionSyncReduxOptions,
	UpdateCollectionSyncReduxOptions
} from '../slice/types';
import { CollectionSyncReducer, SyncReducer, UnionedCollectionSyncReducer } from './types';
import { Draft, produce } from 'immer';

export function createAddCollectionReducer<N extends string, T, RQ>(
	options: AddCollectionSyncSliceOptions<N, T, RQ>
): CollectionSyncReducer<SyncVerb.Add, N, T, RQ> {
	return function(state = options.state, action) {
		return produce(state, (draft) => {
			if (draft.data) {
				const added = options.add(action.data as RQ, draft.data.results);

				if (Array.isArray(added)) {
					draft.data.results = added;
				} else {
					draft.data.results.push(added);
				}
			}
		});
	};
}

export function createRemoveCollectionReducer<N extends string, T, RQ>(
	options: RemoveCollectionSyncReduxOptions<N, T, RQ>
): CollectionSyncReducer<SyncVerb.Remove, N, T, RQ> {
	return function(state = options.state) {
		return produce(state, (draft) => {
			if (draft.data) {
				draft.data.results = [];
			}
		});
	};
}

export function createTargetedRemoveCollectionReducer<N extends string, T, RQ>(
	options: RemoveCollectionSyncReduxOptions<N, T, RQ>
): CollectionSyncReducer<SyncVerb.Remove, N, T, RQ> {
	return function(state = options.state, action) {
		return produce(state, (draft) => {
			if (options.target && draft.data?.results) {
				const shouldRemove = options.target(action.data as RQ);
				draft.data.results = draft.data.results.filter((result) => !shouldRemove(result as T));
			}
		});
	};
}

export function createUpdateCollectionReducer<N extends string, T, RQ>(
	options: UpdateCollectionSyncReduxOptions<N, T, RQ>
): CollectionSyncReducer<SyncVerb.Update, N, T, RQ> {
	return function(state = options.state, action) {
		if (!state.data?.results) {
			return state;
		}

		return produce(state, (draft) => {
			const update = options.update(action.data as RQ);
			draft.data?.results.map(update);
		});
	};
}

export function createTargetedUpdateCollectionReducer<N extends string, T, RQ>(
	options: UpdateCollectionSyncReduxOptions<N, T, RQ>
): CollectionSyncReducer<SyncVerb.Update, N, T, RQ> {
	return function(state, action) {
		return produce(state, (draft) => {
			if (!options.target || !draft?.data?.results) {
				return;
			}

			const update = options.update(action.data as RQ);
			const getTarget = options.target(action.data as RQ);
			const targetIndex = draft.data?.results.findIndex(getTarget) ?? -1;

			if (targetIndex !== -1) {
				draft.data.results[ targetIndex ] = update(draft.data.results[ targetIndex ]);
			}
		});
	};
}

export function getCollectionSyncReducer<SV extends SyncVerb, N extends string, T, RQ>(
	options: UnionedCollectionSyncReduxOptions<N, T, RQ>
): CollectionSyncReducer<SV, N, T, RQ> {
	let reducer: UnionedCollectionSyncReducer<N, T, RQ>;

	switch (options.syncVerb) {
		case SyncVerb.Add:
			reducer = createAddCollectionReducer(options);
			break;
		case SyncVerb.Remove:
			reducer = options.target ?
				createTargetedRemoveCollectionReducer(options) :
				createRemoveCollectionReducer(options);
			break;
		case SyncVerb.Update:
			reducer = options.target ?
				createTargetedUpdateCollectionReducer(options) :
				createUpdateCollectionReducer(options);
			break;
		default:
			reducer = (state: CollectionSyncState<T>) => state;
	}

	return reducer as CollectionSyncReducer<SV, N, T, RQ>;
}

export function createCollectionSyncReducer<SV extends SyncVerb, N extends string, T, RQ>(
	options: UnionedCollectionSyncReduxOptions<N, T, RQ>
): CollectionSyncReducer<SV, N, T, RQ> {
	const reducer = getCollectionSyncReducer(options);
	const type = createSyncActionTypeValue<SV, N>({
		name: options.name,
		syncVerb: options.syncVerb as SV
	});

	return function(state = options.state, action) {
		if (action.type !== type) {
			return state;
		}

		return reducer(state, action);
	} as CollectionSyncReducer<SV, N, T, RQ>;
}

export function createSyncReducer<SV extends SyncVerb, N extends string, T, RQ>(
	options: CreateSyncReduxOptions<SV, N, T, RQ>
): SyncReducer<SV, N, T, RQ> {
	const { transform } = options;
	const type = createSyncActionTypeValue<SV, N>({
		name: options.name,
		syncVerb: options.syncVerb as SV
	});

	return function(state = options.state, action) {
		return produce(state, (draft) => {
			if (action.type === type && draft.data) {
				draft.data = (
					transform ?
						transform(action.data as RQ, draft.data as Draft<T>) :
						action.data
				) as typeof draft.data;
			}
		});
	} as SyncReducer<SV, N, T, RQ>;
}
